import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ActivityIndicator,
  Alert
} from 'react-native';
import { CheckCircle, ArrowRight } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { router, useLocalSearchParams } from 'expo-router';
import { logger } from '@/utils/logger';
import { auth } from '@/utils/firebaseConfig';

export default function SuccessScreen() {
  const { colors } = useTheme();
  const { user, refreshUserProfile } = useAuth(); // Get refresh function
  const { session_id } = useLocalSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);

  useEffect(() => {
    // Verificar o status da sessão de pagamento
    const verifyPayment = async () => {
      if (!session_id || !user?.id) {
        logger.debug('Missing session_id or user, skipping verification');
        setPaymentVerified(true);
        setIsVerifying(false);
        return;
      }

      try {
        logger.debug('Verifying payment session via API', { session_id });

        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          throw new Error('Sessão inválida');
        }

        // Call our new verification API
        const response = await fetch('/api/verify-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sessionId: session_id,
            userId: user?.id
          })
        });

        const data = await response.json();

        if (response.ok && data.verified) {
          logger.info('Payment verified via API', data);

          // FORCE REFRESH OF LOCAL USER STATE
          await refreshUserProfile();

          setPaymentVerified(true);
        } else {
          throw new Error(data.error || 'Payment not verified');
        }

      } catch (error) {
        logger.error('Payment verification failed', { error });
        Alert.alert(
          'Aviso',
          'Não conseguimos confirmar a ativação automaticamente. Se o plano não aparecer, entre em contato ou faça login novamente.',
          [
            {
              text: 'Entendi',
              onPress: () => {
                setPaymentVerified(true);
              }
            }
          ]
        );
      } finally {
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [session_id, user?.id]);

  const handleContinue = () => {
    // Navegar para a tela principal do app
    router.replace('/');
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    content: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 32,
    },
    iconContainer: {
      marginBottom: 32,
      padding: 20,
      borderRadius: 100,
      backgroundColor: colors.success + '20', // 20% opacity
    },
    title: {
      fontSize: 28,
      fontWeight: 'bold',
      color: colors.text,
      textAlign: 'center',
      marginBottom: 16,
    },
    subtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 24,
      marginBottom: 48,
    },
    button: {
      backgroundColor: colors.primary,
      paddingVertical: 16,
      paddingHorizontal: 32,
      borderRadius: 12,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 200,
    },
    buttonText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: 'bold',
      marginRight: 8,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    loadingText: {
      fontSize: 16,
      color: colors.textSecondary,
      marginTop: 16,
    },
  });

  if (isVerifying) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Verificando pagamento...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        <View style={styles.iconContainer}>
          <CheckCircle size={80} color={colors.success} />
        </View>

        <Text style={styles.title}>
          {paymentVerified ? 'Pagamento Aprovado!' : 'Assinatura Ativada!'}
        </Text>

        <Text style={styles.subtitle}>
          Obrigado{user?.nome ? `, ${user.nome}` : ''}! Seu plano foi ativado com sucesso.
          Agora você tem acesso a todos os recursos premium do Põe no Racha.
        </Text>

        <TouchableOpacity
          style={styles.button}
          onPress={handleContinue}
          activeOpacity={0.8}
        >
          <Text style={styles.buttonText}>Começar a Usar</Text>
          <ArrowRight size={20} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}
